import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

//Custom Components
import useFormController from "@/components/useFormController";
import FormButton from "@/ui/Form/FormButton";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import objektiService from "@/services/objektiService";
import MultiLineControl from "@/controls/MultiLineControl";

//Material-UI Icons
import UpdateIcon from "@mui/icons-material/Update";

// MUI
import Tooltip from "@mui/material/Tooltip";

//Types
import { FormControllerProps } from "@/@types/components/formController";
import { GridItem } from "@/ui/Grid";
import CustomButtonGroup from "@/ui/CustomButtonGroup/CustomButtonGroup";
import { IButtonConfig } from "@/@types/ui/CustomButtonGroup";

import { IFieldMultiline } from "@/@types/models/model";
import FormContent from "@/components/FormContent";

const napomenaButtons = [
  { label: "napomena.rusevina_short", ttip: "napomena.rusevina", value: 1 },
  {
    label: "napomena.nije_nekretnina_short",
    ttip: "napomena.nije_nekretnina",
    value: 2,
  },
  { label: "napomena.losa_slika_short", ttip: "napomena.losa_slika", value: 3 },
  {
    label: "napomena.dodati_tlocrt_short",
    ttip: "napomena.dodati_tlocrt",
    value: 4,
  },
  {
    label: "napomena.kriva_geometrija_short",
    ttip: "napomena.kriva_geometrija",
    value: 5,
  },
  {
    label: "napomena.uptitna_namjena_short",
    ttip: "napomena.uptitna_namjena",
    value: 6,
  },
  { label: "napomena.zapusteno_short", ttip: "napomena.zapusteno", value: 7 },
  {
    label: "napomena.u_izgradnji_short",
    ttip: "napomena.u_izgradnji",
    value: 8,
  },
  { label: "napomena.vikendica_short", ttip: "napomena.vikendica", value: 9 },
] as IButtonConfig[];

function ObjectAnalysisInfoForm(props: any) {
  const {
    dc,
    form,
    mode,
    columns = 1,
    recordId,
    isDisabled,
    setNapomenaValue,
    onSave,
    revisionCounter,
    submitValue,
    simpleNapomena = false,
    noPadding = false,
  } = props;
  const snackbarContext = useContext(SnackbarContext);
  const { t } = useTranslation();
  const [selectedButtons, setSelectedButtons] = useState<boolean[]>(
    napomenaButtons.map(() => false)
  );
  const [events, setEvents] = useState<boolean>(false);

  const napomenaInputRef = useRef<HTMLInputElement>();

  const {
    record,
    originalRecord,
    dataChanged,
    onFieldChange,
    doRefresh,
    validate,
    updateOriginalRecord,
  } = useFormController(props as FormControllerProps);

  useEffect(() => {
    if (recordId) {
      if (events) {
        unregisterEvents();
      }
      registerEvents();
      setEvents(true);
      return () => unregisterEvents();
    }
    return () => {};
  }, [record, recordId, dataChanged]);

  const eventRegistrationFn = (evt: KeyboardEvent) => {
    if (evt.altKey) {
      switch (evt.code) {
        case "KeyW":
          if (!isDisabled && dataChanged) {
            handleUpdate();
          }
          break;
      }
    }
  };

  const registerEvents = () => {
    window.addEventListener("keydown", eventRegistrationFn);
  };

  const unregisterEvents = () => {
    window.removeEventListener("keydown", eventRegistrationFn);
  };

  useEffect(() => {
    if (revisionCounter > 0) {
      updateOriginalRecord();
    }
  }, [revisionCounter]);

  useEffect(() => {
    doRefresh();
  }, [recordId]);

  useEffect(() => {
    if (record) {
      const { napomena = "" } = record;
      checkSelectedButtons(napomena as string);
    }
  }, [record]);

  const fieldNapomena = useMemo(() => {
    return {
      title: "Napomena",
      source: "napomena",
      ttoken: "objekti.napomena",
      type: "multiline",
      rows: 2,
      maxRows: 2,
      sort: true,
      saveSpace: true,
      validation: {
        maxLength: 255,
      },
    } as IFieldMultiline;
  }, []);

  const checkSelectedButtons = (value: string) => {
    const newSelectedButtons: boolean[] = napomenaButtons.map(
      (btn: IButtonConfig) =>
        value ? value.indexOf(t(btn.ttip as string)) !== -1 : false
    );
    setSelectedButtons(newSelectedButtons);
  };

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
    if (source === "napomena") {
      checkSelectedButtons(value);
    }
    setNapomenaValue(
      source === "napomena" ? value : record["napomena"],
      originalRecord["napomena"]
    );
  };

  const checkNapomenaContent = (btn: IButtonConfig, napomena: string) => {
    /*let index = -1;
    for (let i = 0; i < napomenaButtons.length; ++i) {
      if (btn.value === napomenaButtons[i].value) {
        index = i;
        break;
      }
    }
    if (index === -1) {
      console.error("Shoulnd't have happened!");
    }*/

    const x = napomena.split(";");
    const b1 = `${t(btn.ttip || "")}`;
    const b2 = `${t(btn.ttip || "")} -`;
    for (let i = 0; i < x.length; i++) {
      const xi = x[i].trim();
      //if (xi === b1) return true;
      //if (xi === b2) return true;
      if (xi.startsWith(b1)) return true;
    }
    return false; //napomena.trim().endsWith(`${t(btn.ttip || '')} -`);
  };

  const removeFromNapomena = (btn: IButtonConfig, napomena: string) => {
    /*const str = `${t(btn.ttip || '')} - `;
    let newNapomena = napomena.slice(0, napomena.length - str.length);
    if (newNapomena[newNapomena.length - 2] === ';') {
      newNapomena = newNapomena.slice(0, newNapomena.length - 2);
    }*/

    let newNapomena = "";
    const x = napomena.split(";");
    const b1 = `${t(btn.ttip || "")}`;
    const b2 = `${t(btn.ttip || "")} -`;
    for (let i = 0; i < x.length; i++) {
      const xi = x[i].trim();
      if (xi === b1) continue;
      if (xi === b2) continue;
      if (newNapomena.length > 0) {
        newNapomena += "; ";
      }
      newNapomena += xi;
    }
    if (onFieldChange) {
      onFieldChange(newNapomena, "napomena");
      setNapomenaValue(newNapomena, originalRecord["napomena"]);
    }
  };

  const addToNapomena = (btn: IButtonConfig, napomena: string) => {
    let newNapomena = (napomena || "") as string;
    const ttipValue = t(btn.ttip || "");
    if (newNapomena.length) {
      newNapomena += `; ${ttipValue} - `;
    } else {
      newNapomena = `${ttipValue} - `;
    }
    if (onFieldChange) {
      onFieldChange(newNapomena, "napomena");
      setNapomenaValue(newNapomena, originalRecord["napomena"]);
    }
  };

  const handleButtonClick = (btn: IButtonConfig) => {
    const { value, label, ttip } = btn;
    const { napomena } = record;
    const shouldRemove = checkNapomenaContent(btn, (napomena as string) || "");

    if (shouldRemove) {
      removeFromNapomena(btn, (napomena as string) || "");
    } else {
      addToNapomena(btn, (napomena as string) || "");
    }
    if (napomenaInputRef.current) {
      napomenaInputRef.current.focus();
    }
  };

  function handleUpdate() {
    const { napomena, gl_obj } = record;
    const isValid = validate();
    if (!isValid) return;

    objektiService
      .partiallyUpdateObjekt(recordId, {
        napomena,
      })
      .then((result) => {
        if (result.success) {
          //console.log("!! Great success");
          snackbarContext.showNotification("update", "success");
          if (onSave) {
            onSave(napomena);
          }
        } else if (result.error) {
          const msg = "Greška prilikom ažuriranja podataka! " + result.error;
          snackbarContext.showNotification(msg, "error");
        } else if (result.validationPass === false) {
          //do nothing
        }
      })
      .finally(() => {
        updateOriginalRecord();
      });
  }

  return (
    <>
      <GridItem xs={3} style={{ paddingBottom: "8px" }}>
        <CustomButtonGroup
          buttonConfig={napomenaButtons}
          handleClick={handleButtonClick}
          verySmall
          selectedButtons={selectedButtons}
          disabled={!recordId || isDisabled}
        />
      </GridItem>
      <GridItem xs={9} style={{ textAlign: "center" }}>
        {mode === "update" ? (
          <Tooltip
            title={
              !recordId || !dataChanged || isDisabled
                ? ""
                : (t("tooltips.shortcuts.save_napomena") as string)
            }
          >
            <FormButton
              variant="contained"
              endIcon={<UpdateIcon />}
              onClick={handleUpdate}
              disabled={!recordId || !dataChanged || isDisabled}
            >
              {t("buttons.update")}
            </FormButton>
          </Tooltip>
        ) : null}
      </GridItem>
      <GridItem xs={12}>
        {!simpleNapomena ? (
          <MultiLineControl
            submitValue={submitValue}
            formMode="form"
            controlMode={recordId && !isDisabled ? mode : "view"}
            field={fieldNapomena}
            type="text"
            value={record.napomena ? (record.napomena as string) : ""}
            onChange={handleFieldChange}
            inputRef={napomenaInputRef}
          />
        ) : (
          <FormContent
            fields={dc.getFormFields(form)}
            fieldNames={dc.getFormFieldsNames(form)}
            columns={1}
            mode="simpleView"
            record={record}
          />
        )}
      </GridItem>
    </>
  );
}

export default ObjectAnalysisInfoForm;
